import { EntityKindHandler, type EntityMenuContext, type AddPickerEntry, type ToolbarAction, type ToolbarActionContext, type EntityInteractionContext, type EntityClickResult } from '../EntityKindHandler';
import type { CanvasEntity, ConnectorPosition } from '../../entityTypes';
import { ALL_CONNECTOR_SIDES, getConnectorPointForRect } from '../../entityTypes';
import { CanvasNode } from './TaskNode';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Unlink, Layers, Square, GitBranch, ToggleLeft, ToggleRight } from 'lucide-react';
import { buildLayerSubmenuItems } from '../../hooks/useLayerSubmenu';
import { buildGroupSubmenu, buildMultiDeleteItem, buildColorGridSubmenu, buildBorderColorSubmenu } from '../../lib/contextMenuHelpers';
import { BORDER_COLOR_DEFAULTS, BORDER_WIDTH_OPTIONS } from '../../lib/edgeStyleConstants';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const TASK_BG_COLOR_OPTIONS = [
  { id: 'white', label: t('canvasEntity.colorWhite'), color: '#ffffff' },
  { id: 'gray', label: t('canvasEntity.colorGray'), color: '#f3f4f6' },
  { id: 'blue', label: t('canvasEntity.colorBlue'), color: '#dbeafe' },
  { id: 'green', label: t('canvasEntity.colorGreen'), color: '#dcfce7' },
  { id: 'yellow', label: t('canvasEntity.colorYellow'), color: '#fef9c3' },
  { id: 'red', label: t('canvasEntity.colorRed'), color: '#fee2e2' },
  { id: 'purple', label: t('canvasEntity.colorPurple'), color: '#ede9fe' },
];

const TASK_TEXT_COLOR_OPTIONS = [
  { id: 'dark', label: t('canvasEntity.colorDark'), color: '#1f2937' },
  { id: 'gray', label: t('canvasEntity.colorGray'), color: '#6b7280' },
  { id: 'blue', label: t('canvasEntity.colorBlue'), color: '#2563eb' },
  { id: 'green', label: t('canvasEntity.colorGreen'), color: '#16a34a' },
  { id: 'red', label: t('canvasEntity.colorRed'), color: '#dc2626' },
  { id: 'white', label: t('canvasEntity.colorWhite'), color: '#ffffff' },
];

const FONT_SIZE_OPTIONS = [
  { id: '10', label: '10px' },
  { id: '12', label: '12px' },
  { id: '14', label: '14px' },
  { id: '16', label: '16px' },
];

const FONT_WEIGHT_OPTIONS = [
  { id: 'normal', label: t('canvasEntity.fontNormal') },
  { id: 'bold', label: t('canvasEntity.fontBold') },
];

export class TaskHandler extends EntityKindHandler {
  readonly kind = 'task';
  readonly Component = CanvasNode;
  readonly dataAttribute = 'data-canvas-node';
  canConnect = true;

  getAddPickerEntry(): AddPickerEntry {
    return { label: 'Task', icon: Square, iconColor: '#3b82f6', priority: 10, mode: 'taskPicker' };
  }

  getConnectorPositions(entity: CanvasEntity): ConnectorPosition[] {
    if (entity.connectable === false) return [];
    return ALL_CONNECTOR_SIDES.map(side => ({
      side,
      ...getConnectorPointForRect(entity.x, entity.y, entity.width, entity.height, side),
    }));
  }

  getToolbarActions(ctx: ToolbarActionContext): ToolbarAction[] {
    const { store, entity } = ctx;
    const data = entity.data as any;
    return [
      {
        name: 'backgroundColor',
        label: t('canvasEntity.background'),
        type: 'color',
        options: TASK_BG_COLOR_OPTIONS,
        getValue: () => data.backgroundColor ?? '#ffffff',
        apply: (v) => store.send({ type: 'task.updateData', entityId: entity.id, dataChanges: { backgroundColor: v as string } }),
      },
      {
        name: 'textColor',
        label: t('canvasEntity.textColor'),
        type: 'color',
        options: TASK_TEXT_COLOR_OPTIONS,
        getValue: () => data.textColor ?? '#1f2937',
        apply: (v) => store.send({ type: 'task.updateData', entityId: entity.id, dataChanges: { textColor: v as string } }),
      },
      {
        name: 'borderColor',
        label: t('canvasEntity.borderColor'),
        type: 'color',
        options: BORDER_COLOR_DEFAULTS.filter(c => c.color).map(c => ({ id: c.id, label: c.label, color: c.color })),
        getValue: () => data.borderColor ?? '',
        apply: (v) => {
          const color = v as string;
          if (!color) {
            store.send({ type: 'task.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'task.updateData', entityId: entity.id, dataChanges: { borderColor: color } });
          }
        },
      },
      {
        name: 'borderWidth',
        label: t('canvasEntity.borderWidth'),
        type: 'select',
        options: BORDER_WIDTH_OPTIONS,
        getValue: () => data.borderColor ? String(data.borderWidth ?? 1) : 'none',
        apply: (v) => {
          if (v === 'none') {
            store.send({ type: 'task.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'task.updateData', entityId: entity.id, dataChanges: { borderWidth: Number(v) } });
          }
        },
      },
      {
        name: 'fontSize',
        label: t('canvasEntity.fontSize'),
        type: 'select',
        options: FONT_SIZE_OPTIONS,
        getValue: () => String(data.fontSize ?? 12),
        apply: (v) => store.send({ type: 'task.updateData', entityId: entity.id, dataChanges: { fontSize: Number(v) } }),
      },
      {
        name: 'fontWeight',
        label: t('canvasEntity.fontWeight'),
        type: 'select',
        options: FONT_WEIGHT_OPTIONS,
        getValue: () => data.fontWeight ?? 'normal',
        apply: (v) => store.send({ type: 'task.updateData', entityId: entity.id, dataChanges: { fontWeight: v as 'normal' | 'bold' } }),
      },
      {
        name: 'connectable',
        label: 'Connectors',
        type: 'toggle',
        getValue: () => entity.connectable,
        apply: (v) => store.send({ type: 'entity.setConnectable', entityId: entity.id, connectable: !!v }),
      },
    ];
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity, layers } = ctx;
    const taskId = entity.id;
    const data = entity.data as any;
    const items: ContextMenuItem[] = [];

    // --- Hintergrundfarbe ---
    items.push(buildColorGridSubmenu({
      id: 'bg-color-submenu',
      label: t('canvasEntity.backgroundColor'),
      currentColor: data.backgroundColor,
      handlerColors: [...TASK_BG_COLOR_OPTIONS],
      onSelectColor: (color) => store.send({ type: 'task.updateData', entityId: taskId, dataChanges: { backgroundColor: color } }),
    }));

    // --- Textfarbe ---
    items.push(buildColorGridSubmenu({
      id: 'text-color-submenu',
      label: t('canvasEntity.textColor'),
      currentColor: data.textColor,
      handlerColors: [...TASK_TEXT_COLOR_OPTIONS],
      onSelectColor: (color) => store.send({ type: 'task.updateData', entityId: taskId, dataChanges: { textColor: color } }),
    }));

    // --- Rahmenfarbe ---
    items.push(buildBorderColorSubmenu({
      id: 'border-color-submenu',
      label: t('canvasEntity.borderColor'),
      currentColor: data.borderColor,
      onSelectColor: (color) => store.send({ type: 'task.updateData', entityId: taskId, dataChanges: { borderColor: color } }),
      onRemoveBorder: () => store.send({ type: 'task.updateData', entityId: taskId, dataChanges: { borderColor: undefined, borderWidth: undefined } }),
    }));

    // --- Toggle connectors ---
    items.push({
      id: 'toggle-connectors',
      label: entity.connectable ? t('canvasEntity.connectorsOff') : t('canvasEntity.connectorsOn'),
      icon: entity.connectable ? ToggleRight : ToggleLeft,
      separator: true,
    onClick: () => store.send({ type: 'entity.setConnectable', entityId: taskId, connectable: !entity.connectable }),
    });

    // --- Gruppe submenu ---
    items.push(buildGroupSubmenu(ctx));

    // --- Pos. 5: Layer submenu ---
    items.push({
      id: 'layer-submenu',
      label: 'Layer',
      icon: Layers,
      iconColor: '#4f46e5',
      children: buildLayerSubmenuItems(layers, entity.layerId, (layerId) => {
        store.send({ type: 'entity.setLayer', entityId: taskId, layerId });
      }),
    });

    // --- Pos. 6: Dependency explorer ---
    if (ctx.onActivateDepExplorer) {
      items.push({
        id: 'dep-explorer',
        label: t('canvasEntity.showDependencies'),
        icon: GitBranch,
        iconColor: '#f59e0b',
        separator: true,
        onClick: () => ctx.onActivateDepExplorer!(entity.id),
      });
    }

    // --- Pos. 7: Remove single node ---
    items.push({
      id: 'remove',
      label: t('canvasEntity.removeFromTree'),
      icon: Unlink,
      iconColor: '#ea580c',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: taskId });
        canvasSelectionStore.send({ type: 'entity.toggle', entityId: taskId });
      },
    });

    // --- Pos. 8: Multi-delete ---
    const multiDelete = buildMultiDeleteItem(ctx, t('canvasEntity.nodes'));
    if (multiDelete) {
      items.push(multiDelete);
    }

    return items;
  }

  onEntityClick(ctx: EntityInteractionContext, _shiftKey: boolean): EntityClickResult | null {
    const uiCtx = ctx.uiStore.getSnapshot().context;
    if (uiCtx.depExplorerActive && uiCtx.depExplorerFocusId === null) {
      return {
        afterSelect: () => {
          ctx.uiStore.send({ type: 'depExplorer.setFocus', entityId: ctx.entity.id });
        },
      };
    }
    return null;
  }


}
